import cookies from 'js-cookie';

import type { ILinkTokenStoragePort } from '@identity/application/ports';

/** Name of the session cookie that holds the link token. */
export const LINK_TOKEN_COOKIE = 'parr-tok-link';

/**
 * Name of the session cookie that holds the account id of the open link. It is a separate cookie so
 * the token stays a plain value for the interceptor, and it is not a credential.
 */
export const LINK_USER_COOKIE = 'parr-usr-link';

/** Options shared by both cookies. They must stay identical so the two are always deleted together. */
const cookieOptions = (): { sameSite: 'strict'; secure: boolean } => ({
  sameSite: 'strict',
  secure: window.location.protocol === 'https:',
});

/**
 * Stores the link token and the account id of the open link in session cookies with
 * `SameSite=Strict`.
 *
 * Two details are easy to break by accident. There is no `expires`, which is what makes them session
 * cookies and why an interrupted flow is resumed by opening the link again; and the value is stored
 * plain, not as JSON. The shared cookie helper of the application does the opposite of both, so it
 * cannot be used here.
 */
export const linkTokenCookieStorage: ILinkTokenStoragePort = {
  save: (linkToken: string): void => {
    cookies.set(LINK_TOKEN_COOKIE, linkToken, cookieOptions());
  },

  read: (): string | null => cookies.get(LINK_TOKEN_COOKIE) ?? null,

  saveUserId: (userId: string): void => {
    cookies.set(LINK_USER_COOKIE, userId, cookieOptions());
  },

  readUserId: (): string | null => cookies.get(LINK_USER_COOKIE) ?? null,

  clear: (): void => {
    cookies.remove(LINK_TOKEN_COOKIE);
    cookies.remove(LINK_USER_COOKIE);
  },
};
